import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import { clinicalApi, type CurrentUser, type Role } from "../../lib/api/clinical";
import { formField } from "./format";

const ROLE_LABELS: Record<Role, string> = {
  admin: "系统管理员",
  infection_control: "院感专职人员",
  clinician: "临床查看人员",
  auditor: "审计人员",
};

interface Props {
  currentUser: CurrentUser;
  onPasswordChanged: () => void;
}

export function SettingsPanel({ currentUser, onPasswordChanged }: Props) {
  const [users, setUsers] = useState<CurrentUser[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(async () => {
    if (currentUser.role === "admin") setUsers(await clinicalApi.users());
  }, [currentUser.role]);

  useEffect(() => {
    // Initial server synchronization is the only source of user account state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadUsers();
  }, [loadUsers]);

  async function changePassword(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const current = formField(form, "current_password");
    const next = formField(form, "new_password");
    const confirm = formField(form, "confirm_password");
    if (next.length < 14) {
      setMessage("新密码至少 14 位");
      return;
    }
    if (next !== confirm) {
      setMessage("两次输入的新密码不一致");
      return;
    }
    setBusy(true);
    try {
      await clinicalApi.changePassword(current, next);
      setMessage("密码已修改，请重新登录");
      onPasswordChanged();
    } catch {
      setMessage("修改失败，请检查当前密码");
    } finally {
      setBusy(false);
    }
  }

  async function createUser(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const username = formField(form, "username").trim();
    const displayName = formField(form, "display_name").trim();
    const role = formField(form, "role") as Role;
    const password = formField(form, "password");
    if (!username || !displayName || password.length < 14) {
      setMessage("请完整填写，初始密码至少 14 位");
      return;
    }
    setBusy(true);
    try {
      await clinicalApi.createUser({
        username,
        display_name: displayName,
        role,
        password,
      });
      event.currentTarget.reset();
      await loadUsers();
      setMessage("账号已创建");
    } catch {
      setMessage("创建失败，工号可能已存在");
    } finally {
      setBusy(false);
    }
  }

  async function toggleUser(user: CurrentUser) {
    setBusy(true);
    try {
      await clinicalApi.setUserActive(user.id, !user.is_active);
      await loadUsers();
      setMessage(user.is_active ? "账号已停用" : "账号已启用");
    } catch {
      setMessage("操作失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view settings-view">
      <section className="predict-form-card">
        <h2>修改我的密码</h2>
        <form className="predict-form" onSubmit={(event) => void changePassword(event)}>
          <label className="full">
            当前密码
            <input name="current_password" type="password" autoComplete="current-password" />
          </label>
          <label className="full">
            新密码（至少14位）
            <input name="new_password" type="password" autoComplete="new-password" />
          </label>
          <label className="full">
            再次输入新密码
            <input name="confirm_password" type="password" autoComplete="new-password" />
          </label>
          <button type="submit" className="primary full" disabled={busy}>
            修改密码
          </button>
        </form>
      </section>

      {currentUser.role === "admin" ? (
        <>
          <section className="predict-form-card">
            <h2>创建院感账号</h2>
            <form className="predict-form" onSubmit={(event) => void createUser(event)}>
              <label>
                工号
                <input name="username" placeholder="如 ic_001" />
              </label>
              <label>
                姓名
                <input name="display_name" />
              </label>
              <label>
                角色
                <select name="role" defaultValue="infection_control">
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                初始密码（至少14位）
                <input name="password" type="password" autoComplete="new-password" />
              </label>
              <button type="submit" className="primary full" disabled={busy}>
                创建账号
              </button>
            </form>
          </section>

          <section className="full-width">
            <h2>用户管理</h2>
            <table className="queue-table">
              <thead>
                <tr>
                  <th>工号</th>
                  <th>姓名</th>
                  <th>角色</th>
                  <th>状态</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.display_name}</td>
                    <td>{ROLE_LABELS[user.role]}</td>
                    <td>{user.is_active ? "启用" : "停用"}</td>
                    <td>
                      {user.id !== currentUser.id ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            void toggleUser(user);
                          }}
                        >
                          {user.is_active ? "停用" : "启用"}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      ) : null}
      {message ? <p className="drawer-msg">{message}</p> : null}
    </div>
  );
}
